/**
 * AI Event Dispatcher
 *
 * Central event coordination system for AI participants.
 * Dispatches simulation events to registered handlers, enabling:
 * - Automatic AI actions on phase changes
 * - Reflection triggers after conversations
 * - Voting automation
 * - Real-time status updates
 *
 * This is a singleton service - use EventDispatcher.getInstance()
 */

import { EventType } from './types/events.types'
import { logger } from '@/lib/logger'
import type {
  AIEvent,
  EventHandler,
  EventHandlerRegistry,
  PhaseChangeEvent,
  ConversationEndEvent,
  VoteSessionStartEvent,
  KingDecisionAnnouncedEvent,
  ManualReflectionTriggerEvent
} from './types/events.types'

export class EventDispatcher {
  private static instance: EventDispatcher | null = null
  private handlers: EventHandlerRegistry = {}
  private eventLog: AIEvent[] = []
  private maxLogSize = 100 // Keep last 100 events
  private broadcastChannel: BroadcastChannel | null = null

  /**
   * Private constructor - use getInstance()
   */
  private constructor() {
    logger.log('🎯 EventDispatcher initialized')

    // Initialize BroadcastChannel for cross-window communication
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('ai-events')

      // Listen for events from other windows/tabs
      this.broadcastChannel.onmessage = (messageEvent) => {
        logger.log('📡 ========== RECEIVED FROM BROADCAST CHANNEL ==========')
        logger.log('📡 Message type:', messageEvent.data?.type)
        logger.log('📡 Full message:', messageEvent.data)

        // Skip test messages
        if (messageEvent.data?.test) {
          logger.log('📡 Received test message - BroadcastChannel is working!')
          return
        }

        // Ensure it's a valid AI event
        if (!messageEvent.data?.type || !messageEvent.data?.runId) {
          logger.warn('📡 Received invalid message - skipping')
          return
        }

        // Convert timestamp back to Date object
        const aiEvent: AIEvent = {
          ...messageEvent.data,
          timestamp: new Date(messageEvent.data.timestamp)
        }

        // Process event locally (but don't re-broadcast)
        this.processEventLocally(aiEvent)
      }

      logger.log('📡 BroadcastChannel initialized for cross-window events')

      // Send test message to verify channel is working
      setTimeout(() => {
        this.broadcastChannel?.postMessage({ test: true, message: 'BroadcastChannel test' })
        logger.log('📡 Sent test message to verify BroadcastChannel')
      }, 1000)
    } else {
      logger.warn('⚠️ BroadcastChannel not supported in this browser')
    }
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): EventDispatcher {
    if (!EventDispatcher.instance) {
      EventDispatcher.instance = new EventDispatcher()
    }
    return EventDispatcher.instance
  }

  /**
   * Register an event handler
   * @param eventType - Type of event to handle
   * @param handler - Handler function to execute
   */
  public registerHandler(eventType: EventType, handler: EventHandler): void {
    if (!this.handlers[eventType]) {
      this.handlers[eventType] = []
    }

    // Prevent duplicate registrations (React Strict Mode runs useEffect twice)
    const handlers = this.handlers[eventType]!
    if (handlers.includes(handler)) {
      logger.log(`🎯 Handler already registered for ${eventType} - skipping duplicate`)
      return
    }

    handlers.push(handler)
    logger.log(`🎯 Handler registered for ${eventType} (total: ${handlers.length})`)
  }

  /**
   * Unregister a specific event handler
   * @param eventType - Type of event to unregister handler for
   * @param handler - Specific handler function to remove
   * @returns true if handler was found and removed, false otherwise
   */
  public unregisterHandler(eventType: EventType, handler: EventHandler): boolean {
    const handlers = this.handlers[eventType]
    if (!handlers) {
      logger.warn(`🎯 No handlers registered for ${eventType}`)
      return false
    }

    const initialLength = handlers.length
    this.handlers[eventType] = handlers.filter(h => h !== handler)
    const removed = this.handlers[eventType]!.length < initialLength

    if (removed) {
      logger.log(`🎯 Handler unregistered for ${eventType} (remaining: ${this.handlers[eventType]!.length})`)
    } else {
      logger.warn(`🎯 Handler not found for ${eventType}`)
    }

    return removed
  }

  /**
   * Unregister all handlers for an event type
   * @param eventType - Type of event to clear handlers for
   */
  public clearHandlers(eventType: EventType): void {
    delete this.handlers[eventType]
    logger.log(`🎯 Handlers cleared for ${eventType}`)
  }

  /**
   * Dispatch an event to all registered handlers AND broadcast to other windows
   * @param event - Event to dispatch
   */
  public async dispatch(event: AIEvent): Promise<void> {
    logger.log(`\n🎯 ========== EVENT DISPATCHED ==========`)
    logger.log(`🎯 Type: ${event.type}`)
    logger.log(`🎯 Run ID: ${event.runId}`)
    logger.log(`🎯 Timestamp: ${event.timestamp.toISOString()}`)
    logger.log(`🎯 Event Data:`, event)

    // Mark as originating from this tab (allows full processing including queue operations)
    const eventWithFlag: AIEvent = { ...event, _isOriginatingTab: true }

    // Broadcast to other windows/tabs (without the flag, so they skip queue operations)
    if (this.broadcastChannel) {
      try {
        logger.log(`📡 Broadcasting to channel 'ai-events':`, event.type)
        // Broadcast original event WITHOUT _isOriginatingTab flag
        this.broadcastChannel.postMessage(event)
        logger.log(`📡 Event broadcasted successfully`)
      } catch (err) {
        logger.error('📡 Failed to broadcast event:', err)
      }
    } else {
      logger.warn('📡 BroadcastChannel not available - events will not cross windows')
    }

    // Process event locally WITH originating flag (enables queue operations)
    await this.processEventLocally(eventWithFlag)
  }

  /**
   * Process event locally without broadcasting (used when receiving from BroadcastChannel)
   * @param event - Event to process
   */
  private async processEventLocally(event: AIEvent): Promise<void> {
    // Add to event log
    this.logEvent(event)

    // Get handlers for this event type
    const eventHandlers = this.handlers[event.type] || []

    if (eventHandlers.length === 0) {
      logger.log(`🎯 No handlers registered for ${event.type}`)
      logger.log(`🎯 ======================================\n`)
      return
    }

    logger.log(`🎯 Found ${eventHandlers.length} handler(s) for ${event.type}`)

    // Execute all handlers in parallel
    const results = await Promise.allSettled(
      eventHandlers.map(handler => handler(event))
    )

    // Log results
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        logger.log(`🎯 Handler ${index + 1} completed successfully`)
      } else {
        logger.error(`🎯 Handler ${index + 1} failed:`, result.reason)
      }
    })

    logger.log(`🎯 ======================================\n`)
  }

  /**
   * Convenience method: Dispatch phase change event
   */
  public async dispatchPhaseChange(
    runId: string,
    phaseId: string,
    phaseName: string,
    previousPhaseId: string | null = null,
    previousPhaseName: string | null = null
  ): Promise<void> {
    const event: PhaseChangeEvent = {
      type: EventType.PHASE_CHANGE,
      timestamp: new Date(),
      runId,
      phaseId,
      phaseName,
      previousPhaseId,
      previousPhaseName
    }
    await this.dispatch(event)
  }

  /**
   * Convenience method: Dispatch conversation end event
   */
  public async dispatchConversationEnd(
    runId: string,
    meetingId: string,
    participants: string[],
    transcript: Array<{
      speakerId: string
      speakerName: string
      message: string
      timestamp: Date
    }>,
    conversationType: 'clan_council' | 'free_consultation' | 'strategic_meeting'
  ): Promise<void> {
    const event: ConversationEndEvent = {
      type: EventType.CONVERSATION_END,
      timestamp: new Date(),
      runId,
      meetingId,
      participants,
      transcript,
      conversationType
    }
    await this.dispatch(event)
  }

  /**
   * Convenience method: Dispatch vote session start event
   */
  public async dispatchVoteSessionStart(
    runId: string,
    sessionId: string,
    voteType: string,
    targetClanId?: string | null
  ): Promise<void> {
    const event: VoteSessionStartEvent = {
      type: EventType.VOTE_SESSION_START,
      timestamp: new Date(),
      runId,
      sessionId,
      voteType,
      targetClanId
    }
    await this.dispatch(event)
  }

  /**
   * Convenience method: Dispatch King decision announced event
   */
  public async dispatchKingDecisionAnnounced(
    runId: string,
    decisionId: string,
    economicAdvisorRoleId: string,
    seniorJudgeRoleId: string
  ): Promise<void> {
    const event: KingDecisionAnnouncedEvent = {
      type: EventType.KING_DECISION_ANNOUNCED,
      timestamp: new Date(),
      runId,
      decisionId,
      economicAdvisorRoleId,
      seniorJudgeRoleId
    }
    await this.dispatch(event)
  }

  /**
   * Convenience method: Dispatch manual reflection trigger event
   */
  public async dispatchManualReflection(
    runId: string,
    roleId: string,
    reason: string
  ): Promise<void> {
    const event: ManualReflectionTriggerEvent = {
      type: EventType.MANUAL_REFLECTION_TRIGGER,
      timestamp: new Date(),
      runId,
      roleId,
      reason
    }
    await this.dispatch(event)
  }

  /**
   * Get event log (for debugging)
   */
  public getEventLog(): AIEvent[] {
    return [...this.eventLog]
  }

  /**
   * Clear event log
   */
  public clearEventLog(): void {
    this.eventLog = []
    logger.log('🎯 Event log cleared')
  }

  /**
   * Add event to log with size limit
   */
  private logEvent(event: AIEvent): void {
    this.eventLog.push(event)
    if (this.eventLog.length > this.maxLogSize) {
      this.eventLog.shift() // Remove oldest event
    }
  }

  /**
   * Get statistics about dispatched events
   */
  public getStatistics(): {
    totalEvents: number
    eventsByType: Record<string, number>
    recentEvents: AIEvent[]
  } {
    const eventsByType: Record<string, number> = {}

    this.eventLog.forEach(event => {
      eventsByType[event.type] = (eventsByType[event.type] || 0) + 1
    })

    return {
      totalEvents: this.eventLog.length,
      eventsByType,
      recentEvents: this.eventLog.slice(-10) // Last 10 events
    }
  }
}

/**
 * Re-export EventType for convenience
 * Updated: 2025-11-09 - Export EventType to fix dynamic import
 */
export { EventType }

/**
 * Export singleton instance for convenience
 */
export const eventDispatcher = EventDispatcher.getInstance()
